import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { api } from '../api';
import { css, Spinner, Badge } from '../components/UI';
import { hoyISO } from '../utils';

const MES = ['','ENERO','FEBRERO','MARZO','ABRIL','MAYO','JUNIO','JULIO','AGOSTO','SEPTIEMBRE','OCTUBRE','NOVIEMBRE','DICIEMBRE'];

const IMP_RATES = { IVA_16: 0.16, RET_IVA_10667: 0.106667, RET_IVA_16: 0.16, RET_ISR_125: 0.0125, RET_ISR_10: 0.10, RET_4: 0.04, RET_IEPS: 0.265, IEPS_TRAS: 0 };
function calcExcel(monto, claves = [], importes = null) {
  const b = monto || 0;
  // Un código cuenta si está en el array `impuestos` O si trae importe exacto en el XML
  // (así el neto cuadra con netoDe aunque el array quedara incompleto).
  const inc = (c) => claves.includes(c) || (importes && importes[c] != null);
  // Importe exacto del XML si existe; si no, tasa × base (captura manual)
  const v = (c) => inc(c) ? (importes && importes[c] != null ? importes[c] : b * IMP_RATES[c]) : '';
  const iva      = v('IVA_16');
  // La columna de IVA retenido suma la retención de honorarios (10.67%) y la de 16%
  const montoDe = (c) => importes && importes[c] != null ? importes[c] : b * IMP_RATES[c];
  const retIvaNum = (inc('RET_IVA_10667') ? montoDe('RET_IVA_10667') : 0)
                  + (inc('RET_IVA_16')    ? montoDe('RET_IVA_16')    : 0);
  const retIva   = retIvaNum || '';
  const retIsr125= v('RET_ISR_125');
  const retIsr10 = v('RET_ISR_10');
  const ret4     = v('RET_4');
  const retIeps  = v('RET_IEPS');
  const iepsTras = v('IEPS_TRAS'); // IEPS trasladado (3%, 8%, 26.5%...): suma, como el IVA
  const total = Math.round((b
    + (iva      || 0)
    + (iepsTras || 0)
    - (retIva   || 0)
    - (retIsr125|| 0)
    - (retIsr10 || 0)
    - (ret4     || 0)
    - (retIeps  || 0)) * 100) / 100;
  return { iva, iepsTras, retIva, retIsr125, retIsr10, ret4, retIeps, total };
}

// Reparte los impuestos entre los conceptos de una solicitud de caja chica o de
// reembolso de gastos, donde cada gasto trae su PROPIA factura. Devuelve una
// función que resuelve concepto por concepto, en este orden:
//   1. Con importes de su XML: se usan exactos (el IVA que se puede acreditar,
//      no la tasa aplicada a la base).
//   2. Factura exenta (su total es igual a su base): sin impuestos. Sin esto una
//      caseta o un flete heredarían a prorrata el IVA de las otras facturas.
//   3. Lo que queda del total de la solicitud sin reclamar por 1, prorrateado
//      entre los conceptos que no tienen XML propio (captura manual, o los de
//      solicitudes anteriores a que esto se guardara por concepto). Así ningún
//      renglón inventa IVA y la columna sigue cuadrando con el neto.
//   4. Si la solicitud no trae ningún importe del XML, las tasas sobre la base.
function repartoImpuestos(conceptos, s) {
  const propios = (c) => (c.impuestosImportes && Object.keys(c.impuestosImportes).length) ? c.impuestosImportes : null;
  const exento  = (c) => c.subTotal != null && c.totalCfdi != null && Math.abs(c.totalCfdi - c.subTotal) < 0.01;
  const suma = s.impuestosImportes || {};
  const resto = {};
  Object.entries(suma).forEach(([k, v]) => { resto[k] = parseFloat(v) || 0; });
  conceptos.forEach(c => {
    const p = propios(c);
    if (p) Object.entries(p).forEach(([k, v]) => {
      if (resto[k] != null) resto[k] = Math.round((resto[k] - (parseFloat(v) || 0)) * 100) / 100;
    });
  });
  const baseResto = conceptos.reduce((a, c) => a + ((propios(c) || exento(c)) ? 0 : (parseFloat(c.monto) || 0)), 0);
  const hayTotales = Object.keys(suma).length > 0;

  return (c) => {
    const p = propios(c);
    if (p) return { claves: c.impuestos?.length ? c.impuestos : Object.keys(p), importes: p };
    if (exento(c)) return { claves: [], importes: null };
    if (hayTotales) {
      const factor = baseResto ? (parseFloat(c.monto) || 0) / baseResto : 0;
      // Los códigos ya agotados por las facturas propias se quedan fuera para
      // que la columna salga vacía en lugar de con un cero.
      const importes = {};
      Object.entries(resto).forEach(([k, v]) => {
        const val = Math.round(v * factor * 100) / 100;
        if (Math.abs(val) > 0.005) importes[k] = val;
      });
      return { claves: Object.keys(importes), importes };
    }
    return { claves: c.impuestos?.length ? c.impuestos : (s.impuestos || []), importes: null };
  };
}

function semana(fechaStr) {
  if (!fechaStr) return '';
  const d = new Date(fechaStr);
  const inicio = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d - inicio) / 86400000 + inicio.getDay() + 1) / 7);
}

function fmtMXN(n) {
  if (!n && n !== 0) return '';
  return Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2 });
}

const ESTADO_COLORS = { pendiente: '#b45309', aprobada: '#047857', por_pagar: '#0e7490', rechazada: '#be123c', pagada: '#1d4ed8' };
const ESTADO_LABELS = { pendiente: 'Validación Contable', aprobada: 'Aprobada', por_pagar: 'Por pagar', rechazada: 'Rechazada', pagada: 'Pagada' };

function fmtFecha(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

function getFechaPago(sol) {
  // Fecha real capturada al subir el comprobante de pago; si falta (documentos
  // antiguos), se infiere del cambio de estado a 'pagada'.
  if (sol.fechaPago) return sol.fechaPago;
  const entry = (sol.historialEstados || []).slice().reverse().find(h => h.estado === 'pagada');
  return entry?.fecha || null;
}

// Fecha (YYYY-MM-DD) en horario local, para el Excel. Evita el corrimiento de día de toISOString (UTC).
function isoLocal(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-CA');
}

// Fecha y hora local (YYYY-MM-DD HH:MM). La hora importa para auditar cuánto
// tardó cada etapa en el mismo día.
function isoLocalHora(d) {
  if (!d) return '';
  const f = new Date(d);
  return `${f.toLocaleDateString('en-CA')} ${f.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
}

// Último paso del historial con ese estado. Se toma el ÚLTIMO y no el primero
// porque si la solicitud se rechazó y se corrigió, lo que vale es la validación
// definitiva, no la del ciclo que se descartó.
const hito = (s, estado) => (s.historialEstados || []).filter(h => h.estado === estado).slice(-1)[0] || null;

// Días entre dos momentos, con un decimal. Vacío si falta alguno de los dos.
const diasEntre = (a, b) => (a && b) ? Math.round(((new Date(b) - new Date(a)) / 86400000) * 10) / 10 : '';

// De dónde salió el tipo de cambio de cada renglón. En pesos va vacío: la
// columna sólo tiene sentido para la moneda extranjera.
const TC_ORIGEN_EXCEL = {
  mxn: '', estimado: 'ESTIMADO', programado: 'PROGRAMADO',
  pagado: 'REAL', sin_tipo_cambio: 'SIN T.C.',
};

// Orígenes de gasto que alimentan el pipeline
const TIPO_META = {
  solicitud:     { label: 'Solicitud',      excel: 'GASTOS_EN_GENERAL', color: '#3d4148', endpoint: '/solicitudes' },
  debito:        { label: 'Débito',         excel: 'DEBITO',        color: '#be123c', endpoint: '/solicitudes', query: 'tipo=debito' },
  contribuciones:{ label: 'Contribuciones', excel: 'CONTRIBUCIONES',color: '#6d28d9', endpoint: '/solicitudes', query: 'tipo=contribuciones' },
  activos_fijos: { label: 'Activos Fijos',  excel: 'ACTIVOS_FIJOS', color: '#0f766e', endpoint: '/solicitudes', query: 'tipo=activos_fijos' },
  mercancias_nacionales: { label: 'Mercancías Nac.', excel: 'MERCANCIAS_NACIONALES', color: '#b45309', endpoint: '/solicitudes', query: 'tipo=mercancias_nacionales' },
  nomina:        { label: 'Pago de Nómina', excel: 'NOMINA',        color: '#4338ca', endpoint: '/solicitudes', query: 'tipo=nomina' },
  caja_chica:    { label: 'Caja Chica',      excel: 'CAJA_CHICA',    color: '#15803d', endpoint: '/solicitudes', query: 'tipo=caja_chica' },
  reembolso_gastos: { label: 'Reembolso Gastos', excel: 'REEMBOLSO_GASTOS', color: '#be185d', endpoint: '/solicitudes', query: 'tipo=reembolso_gastos' },
  complemento_importacion: { label: 'Compl. Importación', excel: 'COMPLEMENTO_IMPORTACION', color: '#0f766e', endpoint: '/solicitudes', query: 'tipo=complemento_importacion' },
  reembolso:    { label: 'Reembolso',    excel: 'REEMBOLSO',    color: '#0369a1', endpoint: '/reembolsos' },
  comprobacion: { label: 'Comprobación', excel: 'COMPROBACION', color: '#047857', endpoint: '/comprobaciones' },
  finiquito:    { label: 'Finiquito',    excel: 'FINIQUITO',    color: '#c4a1ff', endpoint: '/finiquitos' },
};

// Tipos que se capturan como una lista de gastos —cada uno con su motivo, su monto
// y su propia factura—, y el campo donde vive esa lista. El Excel los desglosa en
// una fila por concepto para que cada gasto lleve su cuenta contable.
const CAMPO_CONCEPTOS = {
  caja_chica: 'conceptosCajaChica',
  reembolso_gastos: 'conceptosReembolsoGastos',
  comprobacion: 'conceptosComprobacion',
};

// Los que desglosan por motivo pero SIN factura por renglón: el CFDI, si lo hay, es
// de la solicitud y sus impuestos se prorratean entre los conceptos.
const CAMPO_CONCEPTOS_SIN_CFDI = {
  complemento_importacion: 'conceptosImportacion',
  contribuciones: 'conceptosContribuciones',
};

// Unifica los campos que difieren entre colecciones (comprobaciones no tienen proveedor/motivo)
// Pesos por unidad de la moneda de la solicitud (el backend ya resolvió la
// cascada real → programado → estimado en tipoCambioAplicado). En pesos, 1.
const esExtranjera = (s) => (s.moneda || 'MXN') !== 'MXN';
const tcDe = (s) => (esExtranjera(s) && Number(s.tipoCambioAplicado) > 0 ? Number(s.tipoCambioAplicado) : 1);
const TC_ORIGEN_TEXTO = { estimado: 'T.C. estimado', programado: 'T.C. al programar', pagado: 'T.C. real del pago', sin_tipo_cambio: 'sin tipo de cambio' };

function normalizar(s, tipo) {
  if (tipo === 'comprobacion') {
    return { ...s, _tipo: tipo, proveedor: s.comprobador, motivoPago: s.conceptoGasto, tipoPago: s.tipoPago || 'Programado' };
  }
  return { ...s, _tipo: tipo };
}

function Card({ sol, factura, facturaIdx, totalFacturas }) {
  const monto     = factura ? (factura.monto || 0) : (sol.monto || 0);
  const folioCfdi = factura ? factura.folioCfdi    : sol.folioCfdi;
  const esMulti   = totalFacturas > 1;

  return (
    <div style={{
      background: 'var(--panel)', borderRadius: 10, padding: '12px 14px',
      border: `1px solid ${esMulti ? 'rgba(59,130,246,0.30)' : 'var(--hair)'}`, marginBottom: 10,
      boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
      borderLeft: esMulti ? '3px solid #3b82f6' : undefined,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontFamily: 'Geist Mono, monospace', color: TIPO_META[sol._tipo]?.color || 'var(--plasma)', fontWeight: 700 }}>{sol.folio}</span>
          {sol._tipo && sol._tipo !== 'solicitud' && (
            <span style={{ fontSize: 10, background: `${TIPO_META[sol._tipo].color}18`, color: TIPO_META[sol._tipo].color, padding: '1px 6px', borderRadius: 4, fontWeight: 700, border: `1px solid ${TIPO_META[sol._tipo].color}55` }}>
              {TIPO_META[sol._tipo].label}
            </span>
          )}
          {esMulti && (
            <span style={{ fontSize: 10, background: 'rgba(59,130,246,0.08)', color: '#1d4ed8', padding: '1px 6px', borderRadius: 4, fontWeight: 700, border: '1px solid rgba(59,130,246,0.30)' }}>
              Fact. {facturaIdx + 1}/{totalFacturas}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
          <Badge color={ESTADO_COLORS[sol.estado]}>{ESTADO_LABELS[sol.estado]}</Badge>
          {sol.estado === 'pagada' && (() => {
            const fp = getFechaPago(sol);
            return fp ? (
              <span style={{ fontSize: 10, color: '#1d4ed8', fontFamily: 'Geist Mono, monospace', letterSpacing: '0.02em' }}>
                Pagada {fmtFecha(fp)}
              </span>
            ) : null;
          })()}
        </div>
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: 3 }}>{sol.proveedor}</div>
      <div style={{ fontSize: 11.5, color: 'var(--glass)', marginBottom: folioCfdi ? 2 : 4 }}>{sol.motivoPago}</div>
      {folioCfdi && (
        <div style={{ fontSize: 11, color: 'var(--glass)', fontFamily: 'Geist Mono, monospace', marginBottom: 4 }}>
          CFDI: {folioCfdi}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: 'var(--glass)' }}>{sol.empresa}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', fontFamily: 'Geist Mono, monospace', textAlign: 'right' }}>
          ${fmtMXN(Math.round(monto * tcDe(sol) * 100) / 100)} <span style={{ fontSize: 10, color: 'var(--glass)' }}>{esExtranjera(sol) && tcDe(sol) === 1 ? sol.moneda : 'MXN'}</span>
        </span>
      </div>
      {esExtranjera(sol) && (
        <div style={{ fontSize: 10.5, color: 'var(--glass)', textAlign: 'right', fontFamily: 'Geist Mono, monospace', marginTop: 2 }}
          title={TC_ORIGEN_TEXTO[sol.origenTipoCambio] || ''}>
          {sol.moneda} {fmtMXN(monto)} × {Number(tcDe(sol)).toFixed(4)} ({TC_ORIGEN_TEXTO[sol.origenTipoCambio] || 'T.C.'})
        </div>
      )}
      {sol.observaciones && (
        <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 4, fontStyle: 'italic', borderTop: '1px solid var(--hair-soft)', paddingTop: 4 }}>
          {sol.observaciones}
        </div>
      )}
      <div style={{ fontSize: 10.5, color: 'var(--glass)', marginTop: 4 }}>{sol.sucursal} · {sol.centroCostos}</div>
    </div>
  );
}

export default function Reporte() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({ empresa: '', desde: '', hasta: '', tipoPago: '', origen: '' });

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filtros.empresa) q.set('empresa', filtros.empresa);
      if (filtros.desde) q.set('desde', filtros.desde);
      if (filtros.hasta) q.set('hasta', filtros.hasta);
      // Como el pipeline mezcla todos los estados, cada registro se filtra por SU fecha
      // relevante: programación si está por pagar, pago si ya está pagada, creación
      // en cualquier otro caso (ver aplicarFiltroFecha en el backend).
      q.set('fechaPorEstado', '1');
      q.set('limit', '100000'); // sin truncar: el reporte debe incluir TODOS los registros
      // Carga en paralelo solicitudes, reembolsos, comprobaciones y finiquitos
      const porTipo = await Promise.all(
        Object.entries(TIPO_META).map(([tipo, m]) =>
          api.get(`${m.endpoint}?${q}${m.query ? `&${m.query}` : ''}`)
            .then(d => (d.items || []).map(s => normalizar(s, tipo)))
            .catch(() => [])
        )
      );
      let all = porTipo.flat().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      if (filtros.origen) all = all.filter(s => s._tipo === filtros.origen);
      if (filtros.tipoPago) all = all.filter(s => (s.tipoPago || 'Programado') === filtros.tipoPago);
      setItems(all);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filtros]);

  useEffect(() => { cargar(); }, [cargar]);

  const setF = (k, v) => setFiltros(f => ({ ...f, [k]: v }));

  const programados   = items.filter(s => (s.tipoPago || 'Programado') === 'Programado');
  const noProgramados = items.filter(s => (s.tipoPago || 'Programado') === 'No Programado');

  // Neto a pagar (con IVA/IEPS − retenciones), igual que el Dashboard. El backend ya
  // lo calcula en montoNeto; si falta (colección sin impuestos), usa el monto base.
  // En PESOS: el backend manda montoNetoMXN ya convertido con el tipo de cambio
  // (utils/moneda.js); sin él (colecciones solo en pesos) se usa el neto o la base.
  const netoDeItem = (s) => (s.montoNetoMXN != null ? s.montoNetoMXN : s.montoNeto != null ? s.montoNeto : (s.monto || 0));

  const totalProg   = programados.reduce((a, s) => a + netoDeItem(s), 0);
  const totalNoP    = noProgramados.reduce((a, s) => a + netoDeItem(s), 0);

  const resumenProveedor = Object.values(
    items.reduce((acc, s) => {
      const k = s.proveedor || '—';
      if (!acc[k]) acc[k] = { proveedor: k, count: 0, monto: 0 };
      acc[k].count++;
      acc[k].monto += netoDeItem(s);
      return acc;
    }, {})
  ).sort((a, b) => b.monto - a.monto);

  const [showResumen, setShowResumen] = useState(false);

  const exportarExcel = () => {
    const headers = [
      'Serie', 'FOLIO', 'FECHA DE CREACION', 'AÑO', 'MES', 'DIA', 'SEMANA', 'SEMANA PROGRAMACION', 'FECHA DE PROGRAMACION', 'TIPO ',
      'COMPAÑÍA', 'SUCURSAL', 'Tipo de Solicitud', 'ESTADO', 'FECHA DE PAGO', 'DIRECCION ', 'CENTRO DE COSTOS',
      'SOLICITANTE', 'NO. DE PROVEEDOR', 'PROVEEDOR', 'TIPO DE GASTO',
      'IMPORTE  GASTO', 'IVA', 'IEPS TRASLADADO', 'IVA  RETENCION 0.106667', 'DECUENTO',
      'RETENCION ISR  0.0125', 'ISR RETENCION 0.10 %', 'RETENCION  0.04',
      'RETENCION  IEPS 0.265 ', 'TOTAL', 'DOCUMENTOS', 'CONCEPTO',
      'FORMA DE PAGO', 'METODO DE PAGO', 'USO DE CFDI', 'FOLIO XML', 'FECHA TIMBRADO',
      'CUENTA CONTABLE CONCEPTO', 'CUENTA AGRUPADORA CONCEPTO',
      // Datos fiscales del XML, solo en los CFDI importados desde que se añadieron
      'RFC EMISOR', 'NOMBRE EMISOR', 'FOLIO CFDI', 'FECHA EMISION',
      'SUBTOTAL CFDI', 'DESCUENTO CFDI', 'TOTAL CFDI', 'MONEDA CFDI',
      'TIPO COMPROBANTE', 'FORMA PAGO SAT',
      // Trazabilidad del flujo: quién y cuándo movió la solicitud en cada etapa,
      // reconstruido desde historialEstados. Ver traza().
      'ID', 'FECHA/HORA CREACION', 'ULTIMA ACTUALIZACION', 'EMAIL SOLICITANTE',
      'FECHA VALIDACION CONTABLE', 'VALIDADO POR',
      'FECHA REGISTRO PROGRAMACION', 'PROGRAMADO POR',
      'FECHA REGISTRO PAGO', 'PAGADO POR',
      'DIAS A VALIDAR', 'DIAS A PROGRAMAR', 'DIAS A PAGAR', 'DIAS CICLO TOTAL', 'DIAS EN CURSO',
      'RECHAZOS', 'ETAPA RECHAZO', 'FECHA RECHAZO', 'RECHAZADA POR', 'MOTIVO RECHAZO',
      'BENEFICIARIO', 'BANCO', 'CLABE', 'CUENTA', 'MONEDA',
      'TIPO DE CAMBIO', 'ORIGEN T.C.',
      'NETO SOLICITUD', 'NETO SOLICITUD MXN', 'IMPUESTOS', 'No. FACTURAS',
      'ADJUNTOS', 'TIENE XML', 'TIENE PDF', 'COMPROBANTE DE PAGO',
      'ARCHIVOS', 'COMENTARIOS', 'ULTIMO COMENTARIO', 'HISTORIAL COMPLETO',
    ];

    // Datos fiscales tomados del XML del CFDI. Van vacíos si el renglón no viene
    // de un XML importado: captura manual, o solicitudes previas al cambio.
    // Sirve tanto para una factura como para la comprobación, que los guarda planos.
    // Se prefieren los datos de la propia factura; si esa fila no los trae (fila
    // capturada a mano, o filas rearmadas al alternar el modo de captura), se
    // usan los de la solicitud, que se guardan como respaldo al importar el XML.
    const fiscales = (o, sol) => {
      const d = (o && o.rfcEmisor) ? o : (sol || o || {});
      return [
        d.rfcEmisor || '', d.nombreEmisor || '', d.folioComprobante || '', d.fechaEmision || '',
        d.subTotal ?? '', d.descuento ?? '', d.totalCfdi ?? '', d.monedaCfdi || '',
        d.tipoComprobante || '', d.formaPagoSat || '',
      ];
    };
    // Débito y nómina desglosan por concepto, pero el CFDI es de la solicitud
    // completa: solo se atribuye si hay exactamente una factura, para no mezclar
    // datos de varios comprobantes en un mismo renglón.
    const fiscalesDeSolicitud = (sol) => fiscales(sol.facturas?.length === 1 ? sol.facturas[0] : null, sol);

    // Trazabilidad del flujo (pendiente → aprobada → por pagar → pagada), reconstruida
    // desde historialEstados: fecha y responsable de cada etapa, tiempos entre ellas y
    // rechazos. Son datos de la SOLICITUD, así que se repiten en cada renglón cuando
    // una solicitud se desglosa en varias filas (conceptos o facturas).
    // `primera` marca el primer renglón de la solicitud: el neto solo se escribe ahí,
    // porque es un importe y si se repitiera se sumaría de más en una tabla dinámica.
    const traza = (s, primera) => {
      const aprob = hito(s, 'aprobada');
      const prog  = hito(s, 'por_pagar');
      const pago  = hito(s, 'pagada');
      const rech  = hito(s, 'rechazada');
      const adj   = s.comprobantes || [];
      const hay   = (f) => adj.some(f) ? 'Sí' : 'No';
      // Fecha real del pago si se capturó; si no, el momento del cambio de estado
      const fechaPagoReg = s.fechaPago || pago?.fecha || null;
      return [
        s._id || '', isoLocalHora(s.createdAt), isoLocalHora(s.updatedAt), s.usuario?.email || '',
        isoLocalHora(aprob?.fecha), aprob?.nombreUsuario || '',
        isoLocalHora(prog?.fecha),  prog?.nombreUsuario  || '',
        isoLocalHora(pago?.fecha),  pago?.nombreUsuario  || '',
        diasEntre(s.createdAt, aprob?.fecha),
        diasEntre(aprob?.fecha, prog?.fecha),
        diasEntre(prog?.fecha, fechaPagoReg),
        diasEntre(s.createdAt, fechaPagoReg),
        // Antigüedad: solo tiene sentido mientras la solicitud sigue viva
        ['pagada', 'rechazada'].includes(s.estado) ? '' : diasEntre(s.createdAt, new Date()),
        (s.historialEstados || []).filter(h => h.estado === 'rechazada').length || '',
        s.rechazadaEn || '', isoLocalHora(rech?.fecha), rech?.nombreUsuario || '', s.motivoRechazo || '',
        s.beneficiario || '', s.banco || '', s.clabe || '', s.cuenta || '', s.moneda || '',
        // Tipo de cambio con el que se convierte a pesos, y de dónde salió
        (s.moneda || 'MXN') === 'MXN' ? '' : (s.tipoCambioAplicado ?? ''),
        TC_ORIGEN_EXCEL[s.origenTipoCambio] || '',
        // Los dos netos sólo en el primer renglón: son importes, y repetidos en
        // cada concepto se sumarían de más en una tabla dinámica.
        primera ? (s.montoNeto ?? '') : '',
        primera ? (s.montoNetoMXN ?? '') : '',
        (s.impuestos || []).join(', '), (s.facturas || []).length || '',
        adj.length || '',
        hay(a => a.tipo === 'xml'), hay(a => a.tipo === 'pdf'),
        hay(a => a.concepto === 'Comprobante de pago'),
        adj.map(a => a.nombreOriginal || a.nombre).filter(Boolean).join(' | '),
        (s.comentarios || []).length || '',
        (s.comentarios || []).slice(-1)[0]?.texto || '',
        (s.historialEstados || []).map(h =>
          `${ESTADO_LABELS[h.estado] || h.estado} ${isoLocal(h.fecha)}${h.nombreUsuario ? ` (${h.nombreUsuario})` : ''}`
        ).join(' → '),
      ];
    };

    const dataRows = [];
    let serie = 1;
    // Importes en PESOS: las columnas de importe a TOTAL (21 a 30) se convierten con
    // el tipo de cambio de la solicitud, para que una suma o una tabla dinámica no
    // mezcle dólares con pesos. La moneda, el tipo de cambio, su origen y el neto en
    // moneda original siguen en las columnas del final.
    const COL_IMPORTES = [21, 30];
    const pushFila = (s, fila) => {
      const tc = tcDe(s);
      if (tc !== 1) {
        for (let i = COL_IMPORTES[0]; i <= COL_IMPORTES[1]; i++) {
          if (fila[i] === '' || fila[i] == null || Number.isNaN(Number(fila[i]))) continue;
          fila[i] = Math.round(Number(fila[i]) * tc * 100) / 100;
        }
      }
      dataRows.push(fila);
    };
    items.forEach(s => {
      const fecha = s.fechaSolicitud || (s.createdAt ? s.createdAt.split('T')[0] : '');
      const anio  = fecha ? fecha.split('-')[0] : '';
      const mes   = fecha ? MES[parseInt(fecha.split('-')[1])] || '' : '';
      const dia   = fecha ? parseInt(fecha.split('-')[2]) || '' : '';
      const base  = [
        s.folio || '',
        fecha, anio, mes, dia, semana(fecha),
        s.fechaProgramacion ? semana(isoLocal(s.fechaProgramacion)) : '',
        s.fechaProgramacion ? isoLocal(s.fechaProgramacion) : '',
        s.tipoPago || 'Programado',
        s.empresa || '', s.sucursal || '', TIPO_META[s._tipo]?.excel || 'GASTOS_EN_GENERAL', ESTADO_LABELS[s.estado] || s.estado || '',
        s.estado === 'pagada' ? isoLocal(getFechaPago(s)) : '',
        s.direccion || '', s.centroCostos || '',
        s.usuario?.nombre || '', '',
        s.proveedor || '', s.motivoPago || '',
      ];
      // Caja chica, reembolso de gastos y comprobación se capturan igual: una lista
      // de gastos, cada uno con su motivo y su propia factura. Por eso se desglosan
      // por el mismo camino —una fila por concepto, con su cuenta contable y los
      // impuestos REALES de SU CFDI (ver repartoImpuestos)— en vez de tres copias
      // del mismo bloque, que es como se separan con el tiempo.
      const campoConceptos = CAMPO_CONCEPTOS[s._tipo];
      if (campoConceptos && (s[campoConceptos] || []).length) {
        const conceptos = s[campoConceptos];
        const impuestosDe = repartoImpuestos(conceptos, s);
        // En la comprobación el motivo se lleva la columna TIPO DE GASTO porque es el
        // que trae la cuenta contable, así que el concepto de gasto del comprobador
        // —que es otro dato, y sigue siendo obligatorio— se conserva en CONCEPTO.
        const concepto = s._tipo === 'comprobacion'
          ? [s.conceptoGasto, s.observaciones].filter(Boolean).join(' — ')
          : (s.observaciones || '');
        conceptos.forEach((c, i) => {
          const montoConc = parseFloat(c.monto) || 0;
          const baseConc = [...base];
          baseConc[19] = c.motivo || ''; // índice 19 = motivoPago (TIPO DE GASTO), no proveedor
          const impC = impuestosDe(c);
          const { iva, iepsTras, retIva, retIsr125, retIsr10, ret4, retIeps, total } = calcExcel(montoConc, impC.claves, impC.importes);
          pushFila(s, [serie++, ...baseConc,
            montoConc, iva, iepsTras, retIva, '', retIsr125, retIsr10, ret4, retIeps, total,
            c.folioCfdi || '', concepto,
            s.formaPago || '', c.metodoPago || '', c.usoCfdi || '', c.folioCfdi || '', c.fechaTimbrado || '',
            c.cuentaContable || '', c.cuentaAgrupadora || '', ...fiscales(c, s), ...traza(s, i === 0),
          ]);
        });
        return;
      }
      // Nómina: cada concepto es su propia fila con su motivo y cuenta contable.
      // `base` tiene 19 campos: los últimos son [proveedor, motivoPago].
      // Para cada concepto sustituimos motivoPago (índice 18) y cuentaContable (col CUENTA CONTABLE).
      // Los finiquitos se capturan igual que la nómina (mismo catálogo, mismo campo),
      // así que se desglosan por el mismo camino.
      if ((s._tipo === 'nomina' || s._tipo === 'finiquito') && (s.conceptosNomina || []).length) {
        s.conceptosNomina.forEach((c, i) => {
          const montoConc = (parseFloat(c.monto) || 0) * (c.operacion === 'resta' ? -1 : 1);
          const baseConc = [...base];
          baseConc[19] = c.motivo || ''; // índice 19 = motivoPago (TIPO DE GASTO)
          pushFila(s, [serie++, ...baseConc,
            Math.abs(parseFloat(c.monto) || 0), // IMPORTE GASTO (base sin signo)
            '', '', '', '', '', '', '', '',       // IVA, IEPS y retenciones (nómina no tiene)
            montoConc,                            // TOTAL con signo (+ suma, − resta)
            '', s.observaciones || '',
            s.formaPago || '', '', '', '', '',
            c.cuentaContable || '', c.cuentaAgrupadora || '', ...fiscalesDeSolicitud(s), ...traza(s, i === 0), // columnas extra al final
          ]);
        });
        return;
      }
      // Desglose por motivo SIN factura por renglón: el complemento a costo de
      // importación y las contribuciones. Cada concepto es su propia fila con su
      // motivo y su cuenta contable; el CFDI, cuando lo hay, es de la solicitud, así
      // que sus impuestos se reparten entre los conceptos según su base (con todos
      // a la misma tasa cada renglón queda exacto y la suma cuadra con la factura).
      const campoSinCfdi = CAMPO_CONCEPTOS_SIN_CFDI[s._tipo];
      if (campoSinCfdi && (s[campoSinCfdi] || []).length) {
        const conceptos = s[campoSinCfdi];
        const impuestosDe = repartoImpuestos(conceptos, s);
        // La referencia aduanal y el contenedor identifican el embarque del
        // complemento; en contribuciones no existen y el concepto queda tal cual.
        const refs = [s.referenciaAduanal, s.numeroContenedor].filter(Boolean).join(' · ');
        const concepto = [refs, s.observaciones].filter(Boolean).join(' — ');
        conceptos.forEach((c, i) => {
          const montoConc = parseFloat(c.monto) || 0;
          const baseConc = [...base];
          baseConc[19] = c.motivo || ''; // índice 19 = motivoPago (TIPO DE GASTO)
          const impC = impuestosDe(c);
          const { iva, iepsTras, retIva, retIsr125, retIsr10, ret4, retIeps, total } = calcExcel(montoConc, impC.claves, impC.importes);
          pushFila(s, [serie++, ...baseConc,
            montoConc, iva, iepsTras, retIva, '', retIsr125, retIsr10, ret4, retIeps,
            total,
            s.folioCfdi || '', concepto,
            s.formaPago || '', '', '', '', '',
            c.cuentaContable || '', c.cuentaAgrupadora || '', ...fiscalesDeSolicitud(s), ...traza(s, i === 0),
          ]);
        });
        return;
      }
      // Débito con conceptos: cada concepto es su propia fila con su motivo, IVA y cuenta.
      if (s._tipo === 'debito' && (s.conceptosDebito || []).length) {
        s.conceptosDebito.forEach((c, i) => {
          const montoConc = parseFloat(c.monto) || 0;
          const ivaConc = parseFloat(c.iva) || 0;
          const baseConc = [...base];
          baseConc[19] = c.motivo || ''; // índice 19 = motivoPago (TIPO DE GASTO)
          pushFila(s, [serie++, ...baseConc,
            montoConc, ivaConc, '', '', '', '', '', '', '',
            montoConc + ivaConc,
            '', s.observaciones || '',
            s.formaPago || '', '', '', '', '',
            c.cuentaContable || '', c.cuentaAgrupadora || '', ...fiscalesDeSolicitud(s), ...traza(s, i === 0),
          ]);
        });
        return;
      }
      // Incluye notas de crédito (montos negativos): deben RESTAR, no excluirse
      const facturas = s.facturas?.filter(f => (parseFloat(f.monto) || 0) !== 0);
      if (facturas && facturas.length > 0) {
        // Impuestos EXACTOS del XML a nivel solicitud, prorrateados por la base de cada
        // factura, para que la suma del reporte cuadre con el neto (= Dashboard).
        const ex = calcExcel(s.monto, s.impuestos || [], s.impuestosImportes);
        const totalBase = facturas.reduce((a, f) => a + (parseFloat(f.monto) || 0), 0);
        const prop = (val, factor) => (val === '' || val == null) ? '' : Math.round(val * factor * 100) / 100;
        facturas.forEach((f, i) => {
          const factor = totalBase ? (parseFloat(f.monto) || 0) / totalBase : 1 / facturas.length;
          pushFila(s, [serie++, ...base, f.monto || '',
            prop(ex.iva, factor), prop(ex.iepsTras, factor), prop(ex.retIva, factor), '', prop(ex.retIsr125, factor),
            prop(ex.retIsr10, factor), prop(ex.ret4, factor), prop(ex.retIeps, factor),
            prop(ex.total, factor), f.folioCfdi || '', s.observaciones || '',
            s.formaPago || '', f.metodoPago || '', f.usoCfdi || '', f.folioCfdi || '', f.fechaTimbrado || '',
            s.cuentaContable || '', s.cuentaAgrupadora || '', ...fiscales(f, s), ...traza(s, i === 0)]);
        });
      } else {
        // Importe único: impuestos exactos del XML
        const { iva, iepsTras, retIva, retIsr125, retIsr10, ret4, retIeps, total } = calcExcel(s.monto, s.impuestos || [], s.impuestosImportes);
        pushFila(s, [serie++, ...base, s.monto || '', iva, iepsTras, retIva, '', retIsr125, retIsr10, ret4, retIeps, total, s.folioCfdi || '', s.observaciones || '',
          s.formaPago || '', '', '', s.folioCfdi || '', '',
          s.cuentaContable || '', s.cuentaAgrupadora || '', ...fiscales(s), ...traza(s, true)]);
      }
    });

    const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);

    ws['!cols'] = [
      { wch: 8  }, { wch: 20 }, { wch: 22 }, { wch: 8  }, { wch: 12 }, { wch: 6  }, { wch: 8  }, { wch: 18 }, { wch: 20 }, { wch: 16 },
      { wch: 28 }, { wch: 20 }, { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 24 }, { wch: 30 },
      { wch: 22 }, { wch: 16 }, { wch: 36 }, { wch: 36 },
      { wch: 14 }, { wch: 10 }, { wch: 16 }, { wch: 22 }, { wch: 14 }, { wch: 22 },
      { wch: 20 }, { wch: 16 }, { wch: 22 }, { wch: 14 }, { wch: 28 }, { wch: 40 },
      { wch: 20 }, { wch: 14 }, { wch: 14 }, { wch: 22 }, { wch: 16 }, { wch: 24 }, { wch: 24 },
      // Datos fiscales del XML
      { wch: 16 }, { wch: 36 }, { wch: 14 }, { wch: 14 },
      { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 12 },
      { wch: 18 }, { wch: 16 },
      // Trazabilidad del flujo
      { wch: 26 }, { wch: 18 }, { wch: 18 }, { wch: 28 },
      { wch: 22 }, { wch: 22 },
      { wch: 24 }, { wch: 22 },
      { wch: 20 }, { wch: 22 },
      { wch: 14 }, { wch: 16 }, { wch: 13 }, { wch: 16 }, { wch: 14 },
      { wch: 10 }, { wch: 16 }, { wch: 18 }, { wch: 22 }, { wch: 40 },
      { wch: 28 }, { wch: 24 }, { wch: 22 }, { wch: 22 }, { wch: 9 },
      { wch: 16 }, { wch: 22 }, { wch: 12 },
      { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 20 },
      { wch: 45 }, { wch: 13 }, { wch: 45 }, { wch: 60 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reporte');
    const hoy = hoyISO();
    XLSX.writeFile(wb, `Reporte_Pipeline_${hoy}.xlsx`);
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--ink)' }}>Reporte Pipeline</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--glass)' }}>
            {items.length} registros (solicitudes, débitos, contribuciones, activos fijos, mercancías nac., nómina, reembolsos, comprobaciones y finiquitos) · Total: ${fmtMXN(totalProg + totalNoP)} MXN
          </p>
        </div>
        <button onClick={exportarExcel} disabled={items.length === 0}
          style={{ ...css.btn('#15803d'), display: 'flex', alignItems: 'center', gap: 7, fontSize: 13 }}>
          ⬇️ Exportar Excel
        </button>
      </div>

      {/* Filtros */}
      <div style={{ background: 'var(--panel)', borderRadius: 12, padding: '14px 18px', border: '1px solid var(--hair)', marginBottom: 22, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Compañía</div>
          <input value={filtros.empresa} onChange={e => setF('empresa', e.target.value)}
            placeholder="Filtrar empresa..." style={{ ...css.input(false), width: 200 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Desde</div>
          <input type="date" value={filtros.desde} onChange={e => setF('desde', e.target.value)} style={css.input(false)} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Hasta</div>
          <input type="date" value={filtros.hasta} onChange={e => setF('hasta', e.target.value)} style={css.input(false)} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Tipo</div>
          <select value={filtros.tipoPago} onChange={e => setF('tipoPago', e.target.value)} style={css.select(false)}>
            <option value="">Todos</option>
            <option value="Programado">Programado</option>
            <option value="No Programado">No Programado</option>
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Origen</div>
          <select value={filtros.origen} onChange={e => setF('origen', e.target.value)} style={css.select(false)}>
            <option value="">Todos</option>
            {Object.entries(TIPO_META).map(([t, m]) => <option key={t} value={t}>{m.label}</option>)}
          </select>
        </div>
        <button onClick={cargar} style={css.btn('#44475a')}>Aplicar</button>
        <button onClick={() => setFiltros({ empresa: '', desde: '', hasta: '', tipoPago: '', origen: '' })}
          style={css.btn('#6b7280', true)}>Limpiar</button>
      </div>

      {/* Resumen por proveedor */}
      {!loading && items.length > 0 && (
        <div style={{ background: 'var(--panel)', borderRadius: 12, border: '1px solid var(--hair)', marginBottom: 22, overflow: 'hidden' }}>
          <button onClick={() => setShowResumen(v => !v)} style={{
            width: '100%', padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Inter, sans-serif',
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>📋 Resumen por proveedor ({resumenProveedor.length})</span>
            <span style={{ fontSize: 13, color: 'var(--glass)' }}>{showResumen ? '▲ Ocultar' : '▼ Mostrar'}</span>
          </button>
          {showResumen && (
            <div style={{ borderTop: '1px solid var(--hair-soft)', maxHeight: 320, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--panel)', borderBottom: '1px solid var(--hair)' }}>
                    {['#', 'Proveedor', 'Solicitudes', 'Total MXN', '% del total'].map(h => (
                      <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {resumenProveedor.map((r, i) => {
                    const pct = totalProg + totalNoP > 0 ? (r.monto / (totalProg + totalNoP) * 100).toFixed(1) : 0;
                    return (
                      <tr key={r.proveedor} style={{ borderBottom: '1px solid var(--hair-soft)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,84,28,0.04)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '8px 14px', color: 'var(--glass)', fontSize: 12 }}>{i + 1}</td>
                        <td style={{ padding: '8px 14px', fontWeight: 600, color: 'var(--ink)' }}>{r.proveedor}</td>
                        <td style={{ padding: '8px 14px', color: 'var(--glass)' }}>{r.count}</td>
                        <td style={{ padding: '8px 14px', fontFamily: 'Geist Mono, monospace', fontWeight: 700, color: 'var(--ink)' }}>${fmtMXN(r.monto)}</td>
                        <td style={{ padding: '8px 14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ flex: 1, height: 6, background: 'var(--hair-soft)', borderRadius: 99 }}>
                              <div style={{ width: `${pct}%`, height: '100%', background: 'var(--plasma)', borderRadius: 99 }} />
                            </div>
                            <span style={{ fontSize: 11, color: 'var(--glass)', width: 36 }}>{pct}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {loading ? <Spinner /> : (
        /* Pipeline columns */
        <div className="ui-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 20, overflowWrap: 'anywhere' }}>
          {/* Programado */}
          <div>
            <div style={{ background: 'rgba(255,84,28,0.08)', borderRadius: '10px 10px 0 0', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ color: 'var(--ink)', fontWeight: 700, fontSize: 14 }}>Programado</span>
                <span style={{ marginLeft: 8, background: 'var(--hair)', color: 'var(--ink-soft)', borderRadius: 12, padding: '2px 9px', fontSize: 12, fontWeight: 600 }}>{programados.length}</span>
              </div>
              <span style={{ color: 'var(--ink-soft)', fontSize: 13, fontWeight: 600 }}>${fmtMXN(totalProg)}</span>
            </div>
            <div style={{ background: 'var(--panel)', borderRadius: '0 0 10px 10px', padding: '14px 12px', minHeight: 200, maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>
              {programados.length === 0
                ? <div style={{ textAlign: 'center', color: 'var(--glass)', fontSize: 13, padding: '32px 0' }}>Sin solicitudes</div>
                : programados.flatMap(s => {
                    const facts = s.facturas?.filter(f => (f.monto || 0) > 0);
                    if (facts?.length > 0)
                      return facts.map((f, i) => <Card key={`${s._id}-f${i}`} sol={s} factura={f} facturaIdx={i} totalFacturas={facts.length} />);
                    return [<Card key={s._id} sol={s} />];
                  })
              }
            </div>
          </div>

          {/* No Programado */}
          <div>
            <div style={{ background: 'rgba(124,58,237,0.10)', borderRadius: '10px 10px 0 0', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ color: '#5b21b6', fontWeight: 700, fontSize: 14 }}>No Programado</span>
                <span style={{ marginLeft: 8, background: 'rgba(91,33,182,0.12)', color: '#5b21b6', borderRadius: 12, padding: '2px 9px', fontSize: 12, fontWeight: 600 }}>{noProgramados.length}</span>
              </div>
              <span style={{ color: '#5b21b6', fontSize: 13, fontWeight: 600 }}>${fmtMXN(totalNoP)}</span>
            </div>
            <div style={{ background: 'rgba(99,102,241,0.05)', borderRadius: '0 0 10px 10px', padding: '14px 12px', minHeight: 200, maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>
              {noProgramados.length === 0
                ? <div style={{ textAlign: 'center', color: 'var(--glass)', fontSize: 13, padding: '32px 0' }}>Sin solicitudes</div>
                : noProgramados.flatMap(s => {
                    const facts = s.facturas?.filter(f => (f.monto || 0) > 0);
                    if (facts?.length > 0)
                      return facts.map((f, i) => <Card key={`${s._id}-f${i}`} sol={s} factura={f} facturaIdx={i} totalFacturas={facts.length} />);
                    return [<Card key={s._id} sol={s} />];
                  })
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
